import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Breadcrumbs,
  Collapse,
  Paper,
  Link,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  MenuItem,
  Chip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  Switch,
  Pagination,
  IconButton,
  Grid,
  Container,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Download as DownloadIcon,
  UploadFile as UploadFileIcon,
  Clear as ClearIcon,
  ArrowBack as ArrowBackIcon,
  NavigateNext as NavigateNextIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme, useMediaQuery } from '@mui/material';
import LogoutNavbar from '../../components/LogoutNavbar';
import GlobalSidebar from '../../components/GlobalSidebar';
import PostLoginFooter from '../../components/PostLoginFooter';
import '../../styles/ManageStudents.css';
import api from '../../api';
import { roleOptions } from '../../data/academicOptions';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const normalizeText = (value) => (value ?? '').toString().trim().toLowerCase();

// Club labels sometimes come as "Club Name (College)" or object shapes.
// Canonicalize to a comparable key.
const normalizeClubName = (value) => {
  const text = normalizeText(value).replace(/\s+/g, ' ');
  // strip trailing parenthetical suffix like " (Lucknow University)"
  return text.replace(/\s*\(.*\)\s*$/, '').trim();
};

const ManageStudents = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isTabletDown = useMediaQuery(theme.breakpoints.down('md'));
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const mainContentRef = useRef(null);
  const bulkFileInputRef = useRef(null);
  const [bulkRegisterOpen, setBulkRegisterOpen] = useState(false);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [bulkUploadError, setBulkUploadError] = useState('');
  const [bulkUploadResult, setBulkUploadResult] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const [menuWidths, setMenuWidths] = useState({
    year: null,
    department: null,
    club: null,
    role: null,
    status: null,
  });
  const [openSelectField, setOpenSelectField] = useState(null);

  // Filter states - column specific
  const [filters, setFilters] = useState({
    studentId: '',
    name: '',
    email: '',
    year: '',
    department: '',
    club: '',
    role: '',
    status: ''
  });

  // Sample data with additional fields
 const [users, setUsers] = useState([]);

 const mergeClubLists = (primaryClubs, membershipClubs) => {
   const byKey = new Map();

   [...(primaryClubs || []), ...(membershipClubs || [])].forEach((c) => {
     if (!c) return;
     if (typeof c === 'string') {
       const key = normalizeClubName(c);
       if (!key) return;
       if (!byKey.has(key)) byKey.set(key, c);
       return;
     }

     const name = (c?.name ?? c?.club_name ?? c?.club?.name ?? '').toString().trim();
     const key = normalizeClubName(name);
     if (!key) return;

     const existing = byKey.get(key);
     if (existing && typeof existing === 'object') {
       byKey.set(key, { ...existing, ...c, name });
     } else {
       byKey.set(key, { ...c, name });
     }
   });

   return Array.from(byKey.values());
 };

const [allClubs, setAllClubs] = useState([]);

const fetchClubs = async () => {
  try {
    const response = await api.get('events/club-create-view/');
    const payload = response?.data ?? response;
    const clubsData = Array.isArray(payload)
      ? payload
      : (payload?.results?.data ?? payload?.results ?? payload?.data ?? []);

    const clubs = (Array.isArray(clubsData) ? clubsData : [])
      .map((club) => ({
        id: club?.id,
        name: (club?.name || '').toString().trim(),
        members: Array.isArray(club?.members) ? club.members : [],
      }))
      .filter((c) => c.name.length > 0);

    // Unique by canonical name, keep first
    const byKey = new Map();
    clubs.forEach((c) => {
      const key = normalizeClubName(c.name);
      if (!key) return;
      if (!byKey.has(key)) byKey.set(key, c);
    });

    const unique = Array.from(byKey.values()).sort((a, b) => a.name.localeCompare(b.name));
    setAllClubs(unique);
    return unique;
  } catch (error) {
    // console.log("Error occurred in fetching clubs", error);
    setAllClubs([]);
    return [];
  }
};

const fetchUsers = async (clubsSnapshot) => {
  setLoadingUsers(true);
  try {
    const response = await api.get('users/getallusers/');
    const payload = response?.data ?? response;
    const usersData = Array.isArray(payload)
      ? payload
      : (payload?.results?.data ?? payload?.results ?? payload?.data ?? []);

    const clubsList = Array.isArray(clubsSnapshot) ? clubsSnapshot : allClubs;

    const formattedUsers = (Array.isArray(usersData) ? usersData : []).map((user) => {
      const primaryClubs = Array.isArray(user?.clubs)
        ? user.clubs
        : Array.isArray(user?.club)
          ? user.club
          : (typeof user?.club === 'string' && user.club.trim() !== '' ? [user.club.trim()] : []);

      const userEmail = (user?.email || '').toString().trim();
      const userStudentId = (user?.student_id || user?.studentId || '').toString().trim();

      const membershipClubs = (Array.isArray(clubsList) ? clubsList : [])
        .map((club) => {
          const members = Array.isArray(club?.members) ? club.members : [];
          const match = members.find((m) => {
            if (!m) return false;
            if (typeof m !== 'object') return false;
            const mEmail = (m?.email || '').toString().trim();
            const mId = (m?.member_id ?? m?.memberId ?? '').toString().trim();
            return (userEmail && mEmail === userEmail) || (userStudentId && mId === userStudentId);
          });
          if (!match) return null;
          return {
            name: club?.name,
            status: 'active',
          };
        })
        .filter(Boolean);

      return {
        id: user?.id ?? user?._id ?? user?.student_id ?? user?.email,
        studentId: user?.student_id || user?.studentId || '',
        name: user?.username || user?.name || '',
        email: user?.email || '',
        year: user?.year || '',
        department: user?.department || '',
        clubs: mergeClubLists(primaryClubs, membershipClubs),
        role: user?.role || '',
        status: (user?.status || '').toString().toLowerCase() === 'active' ? 'Active' : 'Inactive',
      };
    });

    setUsers(formattedUsers);
  } catch (error) {
    console.error('Error fetching users:', error);
    setUsers([]);
  } finally {
    setLoadingUsers(false);
  }
};

useEffect(() => {
  const init = async () => {
    const clubs = await fetchClubs();
    await fetchUsers(clubs);
  };
  init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

useEffect(() => {
  // console.log('Updated users:', users);
}, [users]);

// allClubs is now [{id, name, members}] from fetchClubs()

const departments = [
  "Computer Science & Engineering",
  "Information Technology",
  "Electronics & Communication",
  "Electrical Engineering",
  "Mechanical Engineering",
  "Civil Engineering",
  "Chemical Engineering",
  "Biotechnology",
  "Other",
];

const years = ["1st Year", "2nd Year", "3rd Year", "4th Year"];

  const acceptedExcelMimeTypes = new Set([
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel.sheet.macroEnabled.12',
    'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
  ]);

  const isExcelFile = (file) => {
    if (!file) return false;
    const fileName = (file.name || '').toLowerCase();
    const hasExcelExtension = /\.(xlsx|xls|xlsm|xlsb)$/i.test(fileName);
    return hasExcelExtension || acceptedExcelMimeTypes.has(file.type);
  };

  const clearBulkUploadState = () => {
    setBulkUploadFile(null);
    setBulkUploadError('');
    setBulkUploadResult(null);
    setDragActive(false);
    if (bulkFileInputRef.current) {
      bulkFileInputRef.current.value = '';
    }
  };

  const handleBulkFileSelected = (file) => {
    if (!file) return;
    setBulkUploadResult(null);

    if (!isExcelFile(file)) {
      setBulkUploadFile(null);
      setBulkUploadError('Please upload an Excel file only (.xlsx, .xls, .xlsm, .xlsb).');
      if (bulkFileInputRef.current) {
        bulkFileInputRef.current.value = '';
      }
      return;
    }

    setBulkUploadFile(file);
    setBulkUploadError('');
  };

  const handleBulkFileChange = (event) => {
    const file = event.target.files?.[0];
    handleBulkFileSelected(file);
  };

  const handleBulkDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    const file = event.dataTransfer?.files?.[0];
    handleBulkFileSelected(file);
  };

  const handleBulkDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!dragActive) setDragActive(true);
  };

  const handleBulkDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
  };
  // Filter students based on current filters

  const filteredStudents = useMemo(() => users.filter(student => {
    const clubLabels = getClubLabels(student);
    const clubCanonical = clubLabels.map(normalizeClubName);
    const selectedClub = normalizeClubName(filters.club);

    return (
      (!filters.studentId || normalizeText(student.studentId).includes(normalizeText(filters.studentId))) &&
      (!filters.name || normalizeText(student.name).includes(normalizeText(filters.name))) &&
      (!filters.email || normalizeText(student.email).includes(normalizeText(filters.email))) &&
      (!filters.year || student.year === filters.year) &&
      (!filters.department || student.department === filters.department) &&
      (!filters.club || clubCanonical.includes(selectedClub)) &&
      (!filters.role || student.role === filters.role) &&
      (!filters.status || student.status === filters.status)
    );
  }), [users, filters]);

  const roleLabelMap = useMemo(
    () => Object.fromEntries(roleOptions.map(({ value, label }) => [value, label])),
    []
  );

  const studentsPerPage = isTabletDown ? 6 : 10;
  const totalPages = Math.max(1, Math.ceil(filteredStudents.length / studentsPerPage));
  const paginatedStudents = useMemo(() => {
    const start = (page - 1) * studentsPerPage;
    return filteredStudents.slice(start, start + studentsPerPage);
  }, [filteredStudents, page, studentsPerPage]);

  const filteredTotal = filteredStudents.length;
  const rangeStart = filteredTotal === 0 ? 0 : (page - 1) * studentsPerPage + 1;
  const rangeEnd = Math.min(page * studentsPerPage, filteredTotal);

  const handlePageChange = (_event, value) => {
    setPage(value);
  };

  useEffect(() => {
    setPage((prev) => (prev > totalPages ? totalPages : prev));
  }, [totalPages]);

  const handleStatusToggle = (student, nextChecked) => {
    if (!student?.id) return;

    setPendingStatusChange({
      student,
      nextStatus: nextChecked ? 'Active' : 'Inactive',
    });
  };

  const handleCloseStatusDialog = () => {
    if (statusUpdatingId) {
      return;
    }

    setPendingStatusChange(null);
  };

  const handleConfirmStatusToggle = async () => {
    if (!pendingStatusChange?.student?.id) return;

    const { student, nextStatus } = pendingStatusChange;

    setStatusUpdatingId(student.id);

    try {
      await api.patch(`users/update-user/${student.id}/`, {
        status: nextStatus.toLowerCase(),
      });

      setUsers((prev) => prev.map((entry) => (
        entry.id === student.id
          ? { ...entry, status: nextStatus }
          : entry
      )));
      setPendingStatusChange(null);
    } catch (error) {
      console.error('Error updating student status:', error);
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      studentId: '',
      name: '',
      email: '',
      year: '',
      department: '',
      club: '',
      role: '',
      status: ''
    });
  };

  const handleSelectOpen = (field) => (event) => {
    const width = event.currentTarget?.getBoundingClientRect().width;
    if (width) {
      setMenuWidths((prev) => ({ ...prev, [field]: width }));
    }
    setOpenSelectField(field);
  };

  const handleSelectClose = () => {
    setOpenSelectField(null);
  };

  const getMenuProps = (field) => {
    const width = menuWidths[field];
    return {
      disablePortal: false,
      disableScrollLock: true,
      container: mainContentRef.current,
      sx: {
        zIndex: 1300,
      },
      anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
      transformOrigin: { vertical: 'top', horizontal: 'left' },
      marginThreshold: null,
      transitionDuration: 120,
      PaperProps: {
        sx: {
          mt: 0,
          width: width || undefined,
          minWidth: width || undefined,
          maxWidth: width || undefined,
        },
      },
    };
  };

  const exportToPDF = () => {
    const records = filteredStudents;
    const today = new Date();
    const dateStamp = today.toISOString().split('T')[0];
    const timeStamp = today.toLocaleString();

    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });

    const title = 'Students Export';
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text(title, 40, 42);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Generated: ${timeStamp}`, 40, 62);
    doc.text(`Records: ${records.length}`, 40, 78);

    if (activeFilters.length > 0) {
      const filtersLine = `Filters: ${activeFilters.map((f) => f.label).join('  •  ')}`;
      const lines = doc.splitTextToSize(filtersLine, 760);
      doc.text(lines, 40, 94);
    }

    const formatClubsForExport = (student) => {
      if (!student) return '—';
      if (!Array.isArray(student.clubs) || student.clubs.length === 0) return '—';

      return student.clubs
        .map((c) => {
          if (typeof c === 'string') return c;
          const name = c?.name;
          const status = c?.status;
          if (!name) return '';
          return status ? `${name} (${status})` : name;
        })
        .filter((v) => typeof v === 'string' && v.trim() !== '')
        .join(', ');
    };

    const columns = [
      'Student ID',
      'Name',
      'Email',
      'Year',
      'Department',
      'Clubs',
      'Role',
      'Status',
    ];

    const rows = records.map((student) => [
      student.studentId || '—',
      student.name || '—',
      student.email || '—',
      student.year ? `Year ${student.year}` : '—',
      student.department || '—',
      formatClubsForExport(student),
      roleLabelMap[student.role] || student.role || '—',
      student.status || '—',
    ]);

    const tableMarginX = 28;

    autoTable(doc, {
      head: [columns],
      body: rows,
      startY: activeFilters.length > 0 ? 120 : 105,
      theme: 'grid',
      margin: { left: tableMarginX, right: tableMarginX },
      tableWidth: 'auto',
      styles: {
        font: 'helvetica',
        fontSize: 8,
        cellPadding: 4,
        overflow: 'linebreak',
        valign: 'top',
      },
      headStyles: {
        fillColor: [13, 28, 79],
        textColor: 255,
        fontStyle: 'bold',
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252],
      },
      columnStyles: {
        0: { cellWidth: 70 },
        1: { cellWidth: 95 },
        2: { cellWidth: 140 },
        3: { cellWidth: 55 },
        4: { cellWidth: 115 },
        5: { cellWidth: 150 },
        6: { cellWidth: 60 },
        7: { cellWidth: 55 },
      },
      didDrawPage: (data) => {
        const pageCount = doc.getNumberOfPages();
        const pageNumber = doc.getCurrentPageInfo().pageNumber;
        doc.setFontSize(9);
        doc.setTextColor(100);
        doc.text(
          `Page ${pageNumber} of ${pageCount}`,
          doc.internal.pageSize.getWidth() - tableMarginX,
          doc.internal.pageSize.getHeight() - 18,
          { align: 'right' }
        );
      },
    });

    doc.save(`students_export_${dateStamp}.pdf`);
  };

  const activeFiltersCount = Object.values(filters).filter(value => value !== '').length;

  const handleBackNavigation = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/admin/dashboard');
  };

  const handleAddStudent = () => {
    navigate('/admin/students/new');
  };

  const handleToggleBulkRegister = () => {
    setBulkRegisterOpen((prev) => {
      const next = !prev;
      if (!next) {
        clearBulkUploadState();
      }
      return next;
    });
  };

  const handleBulkUpload = async () => {
    if (!bulkUploadFile) {
      setBulkUploadError('Please select an Excel file to upload.');
      return;
    }

    setBulkUploading(true);
    setBulkUploadError('');
    setBulkUploadResult(null);

    try {
      const payload = new FormData();
      payload.append('file', bulkUploadFile);

      const response = await api.post('users/admin/bulk-user-upload/', payload);
      setBulkUploadResult({
        created_users: Array.isArray(response?.created_users) ? response.created_users : [],
        errors: Array.isArray(response?.errors) ? response.errors : [],
      });

      const clubs = await fetchClubs();
      await fetchUsers(clubs);
    } catch (error) {
      const message = error?.error || error?.detail || 'Bulk upload failed. Please try again.';
      setBulkUploadError(message);
    } finally {
      setBulkUploading(false);
    }
  };

  function getClubLabels(student) {
    if (!student) return [];

    if (Array.isArray(student.clubs)) {
      return student.clubs
        .map((c) => {
          if (typeof c === 'string') return c;
          // Support different shapes: {name}, {club_name}, {club: {name}}
          return c?.name ?? c?.club_name ?? c?.club?.name;
        })
        .map((v) => (v ?? '').toString().trim())
        .filter((v) => v !== '');
    }

    return [];
  }

  const activeFilters = useMemo(() => {
    const labelFor = {
      studentId: 'Student ID',
      name: 'Name',
      email: 'Email',
      year: 'Year',
      department: 'Department',
      club: 'Club',
      role: 'Role',
      status: 'Status',
    };

    return Object.entries(filters)
      .filter(([, value]) => value !== '')
      .map(([key, value]) => {
        if (key === 'role') return { key, label: `${labelFor[key]}: ${roleLabelMap[value] || value}` };
        return { key, label: `${labelFor[key]}: ${value}` };
      });
  }, [filters, roleLabelMap]);

  const renderDesktopTable = () => (
    <Paper className="table-paper" elevation={0}>
      <TableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>STUDENT ID</TableCell>
              <TableCell>NAME</TableCell>
              <TableCell>EMAIL</TableCell>
              <TableCell>YEAR</TableCell>
              <TableCell>DEPARTMENT</TableCell>
              <TableCell>CLUB(S)</TableCell>
              <TableCell>ROLE</TableCell>
              <TableCell>STATUS</TableCell>
              <TableCell>ACTIONS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingUsers ? (
              Array.from({ length: studentsPerPage }).map((_, idx) => (
                <TableRow key={`sk-${idx}`} className="table-row">
                  <TableCell><Skeleton width={110} /></TableCell>
                  <TableCell><Skeleton width={140} /></TableCell>
                  <TableCell><Skeleton width={180} /></TableCell>
                  <TableCell><Skeleton width={80} /></TableCell>
                  <TableCell><Skeleton width={160} /></TableCell>
                  <TableCell><Skeleton width={140} /></TableCell>
                  <TableCell><Skeleton width={80} /></TableCell>
                  <TableCell><Skeleton width={70} /></TableCell>
                  <TableCell><Skeleton width={50} /></TableCell>
                </TableRow>
              ))
            ) : paginatedStudents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" className="no-data">
                  <Typography variant="body2" color="text.secondary">
                    No students found matching your filters.
                  </Typography>
                  {activeFiltersCount > 0 && (
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<ClearIcon />}
                      onClick={clearAllFilters}
                      className="ms-clear-filters"
                      sx={{ mt: 1 }}
                    >
                      Clear filters
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              paginatedStudents.map((student) => (
                <TableRow key={student.id} className="table-row">
                  <TableCell sx={{ fontFamily: 'monospace', color: '#2D3748' }}>{student.studentId}</TableCell>
                  <TableCell className="name-cell">{student.name}</TableCell>
                  <TableCell className="email-cell">{student.email}</TableCell>
                  <TableCell className="year-cell">Year {student.year}</TableCell>
                  <TableCell className="department-cell">{student.department}</TableCell>
                  <TableCell className="clubs-cell">
                    {(() => {
                      const clubLabels = getClubLabels(student);
                      if (clubLabels.length === 0) return <span className="ms-muted">—</span>;
                      return (
                        <Box className="ms-club-chips">
                          {clubLabels.slice(0, 2).map((label, idx) => (
                            <Chip key={`${student.id}-club-${idx}`} label={label} size="small" />
                          ))}
                          {clubLabels.length > 2 && (
                            <Chip label={`+${clubLabels.length - 2}`} size="small" variant="outlined" />
                          )}
                        </Box>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="role-cell">{roleLabelMap[student.role] || student.role || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      label={student.status}
                      size="small"
                      className={`status-chip ${student.status.toLowerCase()}`}
                      sx={{
                        backgroundColor: student.status === 'Active' ? '#D1FAE5' : '#FEE2E2',
                        color: student.status === 'Active' ? '#065F46' : '#991B1B',
                        fontWeight: 600,
                        textTransform: 'uppercase'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box className="action-buttons">
                      <Switch
                        checked={student.status === 'Active'}
                        onChange={(event) => handleStatusToggle(student, event.target.checked)}
                        disabled={statusUpdatingId === student.id}
                        color="primary"
                        inputProps={{
                          'aria-label': `Toggle active status for ${student.name || 'student'}`,
                        }}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );

  const renderMobileCards = () => (
    <Box className="student-card-grid">
      {loadingUsers ? (
        Array.from({ length: studentsPerPage }).map((_, idx) => (
          <Paper key={`ms-sk-${idx}`} className="student-card" elevation={0}>
            <Box className="student-card-header">
              <Skeleton width={140} height={18} />
              <Skeleton width={60} height={24} />
            </Box>
            <Box className="student-card-body">
              <Skeleton width={180} height={22} />
              <Skeleton width={240} height={18} />
              <Skeleton width="100%" height={52} />
            </Box>
          </Paper>
        ))
      ) : paginatedStudents.length === 0 ? (
        <Paper className="student-card-empty" elevation={0}>
          <Typography variant="body2" color="text.secondary" align="center">
            No students found matching your filters.
          </Typography>
          {activeFiltersCount > 0 && (
            <Button
              variant="text"
              size="small"
              startIcon={<ClearIcon />}
              onClick={clearAllFilters}
              className="ms-clear-filters"
              sx={{ mt: 1 }}
            >
              Clear filters
            </Button>
          )}
        </Paper>
      ) : (
        paginatedStudents.map((student) => (
          <Paper key={student.id} className="student-card" elevation={0}>
            <Box className="student-card-header">
              <Typography variant="caption" className="student-card-id">
                {student.studentId}
              </Typography>
              <Chip
                label={student.status}
                size="small"
                className={`status-chip ${student.status.toLowerCase()}`}
              />
            </Box>
            <Box className="student-card-body">
              <Box className="student-card-primary">
                <Box>
                  <Typography variant="subtitle1" className="student-card-name">
                    {student.name}
                  </Typography>
                  <Typography variant="body2" className="student-card-email">
                    {student.email}
                  </Typography>
                </Box>
              </Box>
              <Box className="student-card-meta">
                <Box>
                  <Typography variant="caption" color="text.secondary">Year</Typography>
                  <Typography variant="body2">{student.year}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Department</Typography>
                  <Typography variant="body2">{student.department}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Role</Typography>
                  <Typography variant="body2">{roleLabelMap[student.role] || student.role || '—'}</Typography>
                </Box>
              </Box>
              <Box className="student-card-clubs">
                <Typography variant="caption" color="text.secondary" className="student-card-clubs-title">
                  Clubs
                </Typography>
                {(() => {
                  const clubLabels = getClubLabels(student);
                  if (clubLabels.length === 0) {
                    return <Chip label="No Clubs" size="small" variant="outlined" />;
                  }

                  return (
                    <Box
                      sx={{
                        mt: 0.5,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                      }}
                    >
                      {clubLabels.map((label, idx) => (
                        <Chip
                          key={`${student.id}-club-${idx}`}
                          label={label}
                          size="small"
                          sx={{
                            maxWidth: '100%',
                            '& .MuiChip-label': {
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '100%',
                            },
                          }}
                        />
                      ))}
                    </Box>
                  );
                })()}
              </Box>
            </Box>
            <Box className="student-card-footer">
              <Box className="action-buttons">
                <Typography variant="body2" color="text.secondary">
                  {statusUpdatingId === student.id ? 'Updating status...' : 'Active Status'}
                </Typography>
                <Switch
                  checked={student.status === 'Active'}
                  onChange={(event) => handleStatusToggle(student, event.target.checked)}
                  disabled={statusUpdatingId === student.id}
                  color="primary"
                  inputProps={{
                    'aria-label': `Toggle active status for ${student.name || 'student'}`,
                  }}
                />
              </Box>
            </Box>
          </Paper>
        ))
      )}
    </Box>
  );

  return (
    <Box className="page-layout manage-students">
      <LogoutNavbar />
      
      <Box className={`main-layout ${isTabletDown ? 'mobile-main-layout' : ''}`}>
        {!isTabletDown && <GlobalSidebar />}
        
        <Box className={`content-area ${isTabletDown ? 'mobile-content-area' : ''}`}>
          <Container ref={mainContentRef} maxWidth="xl" className="page-container">
        {/* Header */}
        <Box className="ms-page-header">
          <Box className="ms-header-left">
            <Box className="ms-breadcrumb-row">
              <Tooltip title="Back">
                <IconButton onClick={handleBackNavigation} className="ms-back-icon" size="medium" aria-label="Back">
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Breadcrumbs
                className="ms-breadcrumbs"
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
              >
                <Link
                  underline="hover"
                  color="inherit"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/admin/dashboard');
                  }}
                  href="/admin/dashboard"
                  className="ms-breadcrumb-link"
                >
                  Dashboard
                </Link>
                <Typography color="text.primary" className="ms-breadcrumb-current">
                  Students
                </Typography>
              </Breadcrumbs>
            </Box>
            <Box className="ms-title-wrap">
              <Typography variant="h4" className="ms-page-title">
                Manage Students
              </Typography>
              <Typography variant="body2" className="ms-page-subtitle" color="text.secondary">
                Review accounts, filter quickly, export data, and manage active status.
              </Typography>
            </Box>
          </Box>
          <Box className={`ms-header-actions ${isTabletDown ? 'ms-header-actions-mobile' : ''}`}>
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={handleToggleBulkRegister}
              className="ms-bulk-register-button"
            >
              Bulk Student Register
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={exportToPDF}
              className="ms-export-button"
            >
              Export PDF ({filteredStudents.length})
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              className="ms-add-student-button"
              onClick={handleAddStudent}
            >
              Add Student
            </Button>
          </Box>
        </Box>

        <Collapse in={bulkRegisterOpen}>
          <Paper className="ms-bulk-upload-card" elevation={0}>
            <Box className="ms-bulk-upload-header">
              <Box>
                <Typography variant="h6" className="ms-bulk-upload-title">
                  Bulk Student Register
                </Typography>
                <Typography variant="body2" className="ms-bulk-upload-subtitle" color="text.secondary">
                  Upload an Excel file and the backend will run the existing bulk registration flow exactly as implemented.
                </Typography>
              </Box>
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  clearBulkUploadState();
                  setBulkRegisterOpen(false);
                }}
                className="ms-bulk-upload-close"
              >
                Close
              </Button>
            </Box>

            <Box
              className={`ms-bulk-dropzone ${dragActive ? 'drag-active' : ''} ${bulkUploadFile ? 'has-file' : ''}`}
              onClick={() => bulkFileInputRef.current?.click()}
              onDragOver={handleBulkDragOver}
              onDragLeave={handleBulkDragLeave}
              onDrop={handleBulkDrop}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  bulkFileInputRef.current?.click();
                }
              }}
            >
              <input
                ref={bulkFileInputRef}
                type="file"
                accept=".xlsx,.xls,.xlsm,.xlsb,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={handleBulkFileChange}
                hidden
              />
              <UploadFileIcon className="ms-bulk-dropzone-icon" />
              <Typography variant="subtitle1" className="ms-bulk-dropzone-title">
                {bulkUploadFile ? bulkUploadFile.name : 'Drop Excel file here or click to browse'}
              </Typography>
              <Typography variant="body2" className="ms-bulk-dropzone-text" color="text.secondary">
                Accepted formats: .xlsx, .xls, .xlsm, .xlsb
              </Typography>
              <Typography variant="caption" className="ms-bulk-dropzone-hint" color="text.secondary">
                Required columns: email, first_name, last_name, student_id, year, department
              </Typography>
            </Box>

            {bulkUploadError && (
              <Box className="ms-bulk-upload-error">
                <Typography variant="body2">{bulkUploadError}</Typography>
              </Box>
            )}

            {bulkUploadFile && !bulkUploadError && (
              <Box className="ms-bulk-selected-file">
                <Typography variant="body2" className="ms-bulk-selected-file-name">
                  Selected file: {bulkUploadFile.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {(bulkUploadFile.size / 1024).toFixed(1)} KB
                </Typography>
              </Box>
            )}

            <Box className="ms-bulk-upload-actions">
              <Button
                variant="outlined"
                onClick={clearBulkUploadState}
                disabled={bulkUploading && !bulkUploadFile}
                className="ms-bulk-reset-button"
              >
                Clear
              </Button>
              <Button
                variant="contained"
                startIcon={<UploadFileIcon />}
                onClick={handleBulkUpload}
                disabled={!bulkUploadFile || bulkUploading}
                className="ms-bulk-submit-button"
              >
                {bulkUploading ? 'Uploading…' : 'Upload Excel'}
              </Button>
            </Box>

            {bulkUploadResult && (
              <Box className="ms-bulk-result-grid">
                <Paper className="ms-bulk-result-panel success" elevation={0}>
                  <Typography variant="subtitle2" className="ms-bulk-result-heading">
                    Created Users ({bulkUploadResult.created_users.length})
                  </Typography>
                  {bulkUploadResult.created_users.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No new users were created.
                    </Typography>
                  ) : (
                    <Box className="ms-bulk-result-list">
                      {bulkUploadResult.created_users.map((item) => (
                        <Box key={item.email} className="ms-bulk-result-item">
                          <Typography variant="body2" className="ms-bulk-result-primary">{item.email}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Username: {item.username} · Password: {item.password}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Paper>

                <Paper className="ms-bulk-result-panel error" elevation={0}>
                  <Typography variant="subtitle2" className="ms-bulk-result-heading">
                    Errors ({bulkUploadResult.errors.length})
                  </Typography>
                  {bulkUploadResult.errors.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No row-level errors returned by the backend.
                    </Typography>
                  ) : (
                    <Box className="ms-bulk-result-list">
                      {bulkUploadResult.errors.map((item, index) => (
                        <Box key={`${item}-${index}`} className="ms-bulk-result-item">
                          <Typography variant="body2">{item}</Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Paper>
              </Box>
            )}
          </Paper>
        </Collapse>

        {/* Filters Section */}
        <Paper className="ms-filters-card" elevation={0}>
          <Box className="ms-filters-header">
            <Typography variant="h6" className="ms-filters-title">
              Filters
            </Typography>
            <Box className="ms-filters-actions">
              {isTabletDown && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setFiltersOpen((v) => !v)}
                  startIcon={<FilterListIcon />}
                  className="ms-filters-toggle"
                >
                  {filtersOpen ? 'Hide' : 'Show'}
                </Button>
              )}
            </Box>
          </Box>

          <Collapse in={!isTabletDown || filtersOpen}>
            <Grid
              container
              spacing={2}
              columns={{ xs: 12, sm: 12, md: 24 }}
              className="ms-filters-grid"
            >
            <Grid item xs={12} sm={6} md={8}>
              <TextField
                label="Student ID"
                value={filters.studentId}
                onChange={(e) => handleFilterChange('studentId', e.target.value)}
                fullWidth
                size="small"
                variant="outlined"
                sx={{ minWidth: 200 }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={8}>
              <TextField
                label="Name"
                value={filters.name}
                onChange={(e) => handleFilterChange('name', e.target.value)}
                fullWidth
                size="small"
                variant="outlined"
                sx={{ minWidth: 200 }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={8}>
              <TextField
                label="Email"
                value={filters.email}
                onChange={(e) => handleFilterChange('email', e.target.value)}
                fullWidth
                size="small"
                variant="outlined"
                sx={{ minWidth: 200 }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select
                label="Year"
                value={filters.year}
                onChange={(e) => handleFilterChange('year', e.target.value)}
                fullWidth
                size="small"
                variant="outlined"
                SelectProps={{
                  MenuProps: getMenuProps('year'),
                  onOpen: handleSelectOpen('year'),
                  onClose: handleSelectClose,
                  open: openSelectField === 'year',
                }}
                slotProps={{
                  inputLabel: {
                    shrink: true
                  }
                }}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="">All Years</MenuItem>
                {years.map((year) => (
                  <MenuItem key={year} value={year}>Year {year}</MenuItem>
                ))}
              </TextField>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select
                label="Department"
                value={filters.department}
                onChange={(e) => handleFilterChange('department', e.target.value)}
                fullWidth
                size="small"
                variant="outlined"
                SelectProps={{
                  MenuProps: getMenuProps('department'),
                  onOpen: handleSelectOpen('department'),
                  onClose: handleSelectClose,
                  open: openSelectField === 'department',
                }}
                slotProps={{
                  inputLabel: {
                    shrink: true
                  }
                }}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="">All Departments</MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                ))}
              </TextField>
            </Grid>
            
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                select
                label="Club"
                value={filters.club}
                onChange={(e) => handleFilterChange('club', e.target.value)}
                fullWidth
                size="small"
                variant="outlined"
                SelectProps={{
                  MenuProps: getMenuProps('club'),
                  onOpen: handleSelectOpen('club'),
                  onClose: handleSelectClose,
                  open: openSelectField === 'club',
                }}
                slotProps={{
                  inputLabel: {
                    shrink: true
                  }
                }}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="">All Clubs</MenuItem>
                {allClubs.map((club) => (
                  <MenuItem key={club?.id ?? club?.name} value={club?.name ?? ''}>
                    {club?.name ?? ''}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                select
                label="Role"
                value={filters.role}
                onChange={(e) => handleFilterChange('role', e.target.value)}
                fullWidth
                size="small"
                variant="outlined"
                SelectProps={{
                  MenuProps: getMenuProps('role'),
                  onOpen: handleSelectOpen('role'),
                  onClose: handleSelectClose,
                  open: openSelectField === 'role',
                }}
                slotProps={{
                  inputLabel: {
                    shrink: true
                  }
                }}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="">All Roles</MenuItem>
                <MenuItem value="student">Student</MenuItem>
                <MenuItem value="club_member">Club Member</MenuItem>
              </TextField>
            </Grid>
            
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                select
                label="Status"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                fullWidth
                size="small"
                variant="outlined"
                SelectProps={{
                  MenuProps: getMenuProps('status'),
                  onOpen: handleSelectOpen('status'),
                  onClose: handleSelectClose,
                  open: openSelectField === 'status',
                }}
                slotProps={{
                  inputLabel: {
                    shrink: true
                  }
                }}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <Button
                variant="outlined"
                onClick={clearAllFilters}
                fullWidth
                className="ms-clear-inline"
                sx={{ height: '100%', minHeight: 40, minWidth: 160 }}
                disabled={activeFiltersCount === 0}
              >
                Clear
              </Button>
            </Grid>

            </Grid>
          </Collapse>
          
          <Box className="ms-filter-results" aria-live="polite">
            <Typography variant="body2" color="text.secondary">
              Showing {rangeStart}-{rangeEnd} of {filteredTotal} students
              {activeFiltersCount > 0 ? ` • ${activeFiltersCount} filter${activeFiltersCount === 1 ? '' : 's'} active` : ''}
            </Typography>
          </Box>

          {activeFilters.length > 0 && (
            <Box className="ms-active-filters" aria-label="Active filters">
              {activeFilters.map((f) => (
                <Chip
                  key={f.key}
                  label={f.label}
                  size="small"
                  onDelete={() => handleFilterChange(f.key, '')}
                />
              ))}
            </Box>
          )}
        </Paper>

        {/* Table */}
        {!isTabletDown ? renderDesktopTable() : renderMobileCards()}

        {/* Pagination */}
        {filteredStudents.length > 0 && (
          <Box className="pagination-container">
            <Pagination
              count={totalPages}
              page={Math.min(page, totalPages)}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}
                </Container>
        </Box>
      </Box>
      
      <PostLoginFooter />

      <Dialog
        open={Boolean(pendingStatusChange)}
        onClose={handleCloseStatusDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          Confirm Status Change
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {pendingStatusChange
              ? `Are you sure you want to ${pendingStatusChange.nextStatus === 'Active' ? 'activate' : 'deactivate'} ${pendingStatusChange.student?.name || 'this student'}?`
              : ''}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            variant="outlined"
            onClick={handleCloseStatusDialog}
            disabled={Boolean(statusUpdatingId)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmStatusToggle}
            disabled={Boolean(statusUpdatingId)}
          >
            {statusUpdatingId ? 'Updating...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageStudents;